import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useProfileContext } from '../hooks/useProfileContext';
import { apiGetTours, apiCreateTour, apiDeleteTour, apiGetTourBook, apiGetDaysheetLayoutEffective, apiGetTourBookLayoutEffective, apiGetTourBudget, type TourBudgetDTO } from '../api';
import { buildTourBookDoc, exportTourBookPdf } from '../lib/tourbook';
import PdfPreview from '../components/PdfPreview';
import { resolveImageToDataUrl } from './RiderPage';
import { can } from '../lib/permissions';
import { useWriteGate } from '../lib/writeGate';
import TourPromoPanel from '../components/TourPromoPanel';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

interface TourRow {
  id: string | number;
  name: string;
  band_id?: string | null;
  slug?: string;
  show_count?: number;
}

export default function TourListPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profiles, activeProfileId } = useProfileContext();
  // Capability-aware write gate (lot A2). `/api/tours` is mounted AFTER the
  // server's global read-only guard (server/index.js:519-525; routers at
  // :598-599) → create/delete 403 for a read-only account.
  const writeGate = useWriteGate(activeProfileId);
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !writeGate.canWrite;
  const [tours, setTours] = useState<TourRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  // Integrated tour budget: which tour's budget panel is open + cached payloads.
  const [openBudget, setOpenBudget] = useState<string | null>(null);
  const [budgets, setBudgets] = useState<Record<string, TourBudgetDTO>>({});
  const [budgetLoading, setBudgetLoading] = useState<string | null>(null);
  const [budgetError, setBudgetError] = useState<string | null>(null);
  // Per-tour "Promo page" panel (option C, 2026-10-19) — which tour's promo
  // editor is open. The panel itself loads the tour + band blobs lazily.
  const [openPromo, setOpenPromo] = useState<string | null>(null);

  const activeBand = activeProfileId ? profiles.find((p) => p.id === activeProfileId) : null;
  // Tour BUDGET gate (chantier C, 2026-09-25): the `finance.budget` capability
  // — owner/superadmin always hold it (the API returns it in their effective
  // set); an editor/viewer only if granted. Hides the 💰 Budget button + panel;
  // GET /api/tours/:id/budget is 403-guarded the same way.
  const budgetAccess = can(activeBand, 'finance.budget');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // Tours are band-scoped (tenant): only show tours of the active band.
    // A global tour (band_id null) is shown only if the backend allows it, but
    // here we prefer the band's own tours. (2026-08-11)
    apiGetTours()
      .then((data) => setTours((data as TourRow[]).filter((t) => activeProfileId ? String(t.band_id ?? '') === activeProfileId : true)))
      .catch((e) => setError(e?.message || 'Failed to load tours'))
      .finally(() => setLoading(false));
  }, [activeProfileId]);

  useEffect(() => { load(); }, [load]);

  const createTour = async () => {
    if (!writeGate.canWrite) return;
    if (!newName.trim()) return;
    if (!activeBand) { alert(t('tours.selectBandFirst')); return; }
    setCreating(true);
    try {
      const created = await apiCreateTour({ name: newName.trim(), band_id: activeBand.id });
      setNewName('');
      setShowNew(false);
      // Navigate to the filtered shows view for the new tour.
      navigate(`/tour/shows?tour=${encodeURIComponent(String(created.id))}&band=${encodeURIComponent(activeBand.id)}`);
    } catch (e: any) {
      alert(t('tours.createFailed', { msg: e.message }));
    } finally {
      setCreating(false);
    }
  };

  const deleteTour = async (tr: TourRow) => {
    if (!writeGate.canWrite) return;
    if (tr.show_count && tr.show_count > 0) {
      alert(t('tours.hasShows'));
      return;
    }
    if (!(await showConfirm(t('tours.deleteConfirm', { name: tr.name })))) return;
    try {
      await apiDeleteTour(tr.id);
      setTours((prev) => prev.filter((x) => String(x.id) !== String(tr.id)));
    } catch (e: any) {
      alert(t('tours.deleteFailed', { msg: e?.message || e }));
    }
  };

  // Toggle the integrated budget panel for a tour; lazy-fetch (and cache) its
  // per-currency revenue / expenses / net on first open.
  const toggleBudget = async (tr: TourRow) => {
    const key = String(tr.id);
    if (openBudget === key) { setOpenBudget(null); return; }
    setOpenBudget(key);
    if (!budgets[key]) {
      setBudgetLoading(key);
      setBudgetError(null);
      try {
        const data = await apiGetTourBudget(tr.id);
        setBudgets((prev) => ({ ...prev, [key]: data }));
      } catch (e: any) {
        setBudgetError(e?.message || 'Failed to load tour budget');
      } finally {
        setBudgetLoading(null);
      }
    }
  };

  // Aperçu du tour book DANS la page (même patron que l'itinéraire : on construit
  // le docDef et PdfPreview le rend page par page ; aucune génération PDF n'est
  // déclenchée, rien n'est téléchargé). Un seul aperçu ouvert à la fois.
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<any | null>(null);
  const [previewBusy, setPreviewBusy] = useState<string | null>(null);

  // Mêmes entrées que `exportBook` ci-dessous (layout tour book du groupe + son
  // layout day sheet pour les day sheets embarquées) : l'aperçu montre donc
  // EXACTEMENT le document que le bouton d'export produira.
  const togglePreview = async (tr: TourRow) => {
    const key = String(tr.id);
    if (previewKey === key) { setPreviewKey(null); setPreviewDoc(null); return; }
    if (!tr.show_count) { alert(t('tours.noShowsBook')); return; }
    setPreviewKey(key);
    setPreviewDoc(null);
    setPreviewBusy(key);
    try {
      const data = await apiGetTourBook(tr.id);
      const bandId = data.band?.id || tr.band_id || undefined;
      const tourbookLayout = bandId ? await apiGetTourBookLayoutEffective(bandId).catch(() => null) : null;
      const daysheetLayout = bandId ? await apiGetDaysheetLayoutEffective(bandId).catch(() => null) : null;
      const venuePhotoData = await resolveVenuePhotos(data.days || []);
      setPreviewDoc(buildTourBookDoc({
        tour: data.tour,
        band: data.band,
        days: data.days || [],
        layout: tourbookLayout as any,
        daysheetLayout: daysheetLayout as any,
        venuePhotoData,
      }));
    } catch (e: any) {
      setPreviewKey(null);
      alert(t('tours.exportFailed', { msg: e?.message || e }));
    } finally {
      setPreviewBusy(null);
    }
  };

  // Le tour book embarque la photo « document » de chaque salle : on la résout ici
  // (URL → data URL PNG/JPEG) parce que pdfmake n'embarque que des data URLs.
  // Une URL absente ou introuvable ⇒ pas de section pour ce jour.
  const resolveVenuePhotos = async (days: Array<{ venue_photo?: { url?: string } | null }>) => {
    const urls = [...new Set((days || []).map((d) => d?.venue_photo?.url).filter(Boolean) as string[])];
    const pairs = await Promise.all(urls.map(async (u) => [u, await resolveImageToDataUrl(u)] as const));
    return Object.fromEntries(pairs.filter(([, dataUrl]) => !!dataUrl)) as Record<string, string>;
  };

  const [exportingBook, setExportingBook] = useState<string | null>(null);
  const exportBook = async (tr: TourRow) => {
    if (!tr.show_count) { alert(t('tours.noShowsBook')); return; }
    setExportingBook(String(tr.id));
    try {
      const data = await apiGetTourBook(tr.id);
      // Apply the band's effective Tour Book layout (its own sections/texts/style)
      // AND the band's effective daysheet layout (to each embedded daysheet).
      const bandId = data.band?.id || tr.band_id || undefined;
      const tourbookLayout = bandId ? await apiGetTourBookLayoutEffective(bandId).catch(() => null) : null;
      const daysheetLayout = bandId ? await apiGetDaysheetLayoutEffective(bandId).catch(() => null) : null;
      const venuePhotoData = await resolveVenuePhotos(data.days || []);
      const url = await exportTourBookPdf({
        tour: data.tour,
        band: data.band,
        days: data.days || [],
        layout: tourbookLayout as any,
        daysheetLayout: daysheetLayout as any,
        venuePhotoData,
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data.tour?.name || 'tour').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-tour-book.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      alert(t('tours.exportFailed', { msg: e?.message || e }));
    } finally {
      setExportingBook(null);
    }
  };

  return (
    <div ref={denseRef} className="page">
      <PageHeader
        title={withPageIcon('tours.title', t('tours.title'))}
        subtitle={activeBand ? t('tours.forBand', { name: activeBand.name }) : t('tours.selectBand')}
        actions={
          <button className="btn btn-primary" onClick={() => setShowNew((v) => !v)} disabled={!writeGate.canWrite}>
            {t('tours.newTour')}
          </button>
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      {showNew && (
        <div className="card" style={{ maxWidth: 480, marginBottom: 20 }}>
          <div className="form-field" style={{ marginBottom: 10 }}>
            <label>{t('tours.tourName')}</label>
            <input className="form-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('tours.tourNamePh')} autoFocus />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={createTour} disabled={!newName.trim() || creating || !activeBand || !writeGate.canWrite}>
              {creating ? t('tours.creating') : t('tours.createTour')}
            </button>
            <button className="btn" onClick={() => setShowNew(false)}>{t('tours.cancel')}</button>
          </div>
        </div>
      )}

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p className="text-dim">{t('tours.loading')}</p>}

      {!loading && tours.length === 0 && (
        <p className="text-dim">{t('tours.noTours')}</p>
      )}

      {/* P3a: the dense <table className="tbl-dense"> is now one card stack — ONE markup at every
          width, and the budget panel opens UNDER its own card instead of as a
          colspan row (same lazy fetch, same cache, same gates). */}
      <div className="rc-list" data-tours-list>
        {tours.map((tr) => {
          const band = profiles.find((p) => p.id === tr.band_id);
          const budgetKey = String(tr.id);
          const budgetOpen = openBudget === budgetKey;
          const budget = budgets[budgetKey];
          return (
            <div className="rc-list-item" key={budgetKey} data-tour-card={budgetKey}>
              <div
                className="rc-card clickable"
                onClick={() => {
                  const b = tr.band_id || activeBand?.id || '';
                  navigate(`/tour/shows?tour=${encodeURIComponent(String(tr.id))}${b ? `&band=${encodeURIComponent(b)}` : ''}`);
                }}
              >
                <span className="rc-card-ic" aria-hidden="true">🎫</span>
                <div className="rc-card-body">
                  <div className="rc-card-name">{tr.name}</div>
                  <div className="rc-card-meta">
                    <span>{band?.name || '\u2014'}</span>
                  </div>
                </div>
                <span className="rc-chip">{tr.show_count ?? '\u2014'} {t('tours.colShows')}</span>
                <div className="rc-card-actions">
                  {budgetAccess && (
                    <button
                      type="button"
                      title={t('tours.budgetTitle')}
                      aria-label={t('tours.budgetAria', { name: tr.name })}
                      onClick={(e) => { e.stopPropagation(); void toggleBudget(tr); }}
                      className="btn btn-secondary btn-small"
                    >
                      {budgetOpen ? t('tours.budgetHide') : t('tours.budgetShow')}
                    </button>
                  )}
                  {/* Promo page editor (option C): only for a band-scoped tour
                      of the active band — the blob is band-scoped content. */}
                  {tr.band_id && String(tr.band_id) === activeProfileId && (
                    <button
                      type="button"
                      title={t('promoContent.tourPanelTitle')}
                      aria-label={t('promoContent.tourPanelTitle')}
                      onClick={(e) => { e.stopPropagation(); setOpenPromo((cur) => (cur === budgetKey ? null : budgetKey)); }}
                      className="btn btn-secondary btn-small"
                      data-tour-promo-toggle={budgetKey}
                    >
                      {openPromo === budgetKey ? t('promoContent.tourPanelHide') : t('promoContent.tourPanelShow')}
                    </button>
                  )}
                  <button
                    type="button"
                    title={t('tours.previewTitle')}
                    aria-label={t('tours.previewAria', { name: tr.name })}
                    aria-expanded={previewKey === budgetKey}
                    aria-controls={`tour-preview-${budgetKey}`}
                    onClick={(e) => { e.stopPropagation(); void togglePreview(tr); }}
                    disabled={previewBusy === budgetKey}
                    className="btn btn-secondary btn-small"
                    data-tour-preview-toggle={budgetKey}
                  >
                    {previewKey === budgetKey ? t('tours.previewHide') : t('tours.previewShow')}
                  </button>
                  <button
                    type="button"
                    title={t('tours.exportTitle')}
                    aria-label={t('tours.exportAria', { name: tr.name })}
                    onClick={(e) => { e.stopPropagation(); void exportBook(tr); }}
                    disabled={exportingBook === budgetKey}
                    className="btn btn-secondary btn-small"
                  >
                    {exportingBook === budgetKey ? '\u2026' : t('tours.exportBook')}
                  </button>
                  <button
                    type="button"
                    title={t('tours.deleteTitle')}
                    aria-label={t('tours.deleteAria', { name: tr.name })}
                    disabled={!writeGate.canWrite}
                    onClick={(e) => { e.stopPropagation(); deleteTour(tr); }}
                    className="btn btn-small btn-danger"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              {openPromo === budgetKey && tr.band_id && String(tr.band_id) === activeProfileId && (
                <div className="rc-panel" style={{ marginTop: 8 }}>
                  <TourPromoPanel tourId={tr.id} bandId={String(tr.band_id)} canWrite={writeGate.canWrite} />
                </div>
              )}
              {previewKey === budgetKey && (
                <div className="rc-panel" id={`tour-preview-${budgetKey}`} data-tour-preview={budgetKey}>
                  {previewBusy === budgetKey || !previewDoc ? (
                    <p className="text-dim">{t('tours.previewLoading')}</p>
                  ) : (
                    <PdfPreview docDef={previewDoc} />
                  )}
                </div>
              )}
              {budgetAccess && budgetOpen && (
                <div className="rc-panel" data-tour-budget={budgetKey}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <strong>{t('tours.budgetTitle')}</strong>
                    <span className="text-dim" style={{ fontSize: 'var(--font-sm)' }}>{t('tours.budgetSub')}</span>
                  </div>
                  {budgetLoading === budgetKey && (
                    <p className="text-dim">{t('tours.budgetLoading')}</p>
                  )}
                  {budgetError && !budget && (
                    <p style={{ color: 'var(--danger)' }}>{budgetError}</p>
                  )}
                  {budget && (
                    budget.byCurrency.length === 0 ? (
                      <p className="text-dim">{t('tours.budgetEmpty')}</p>
                    ) : (
                      <table className="table table-sm tbl-dense">
                        <thead>
                          <tr>
                            <th>{t('tours.budgetColCurrency')}</th>
                            <th data-num>{t('tours.budgetColRevenue')}</th>
                            <th data-num>{t('tours.budgetColExpenses')}</th>
                            <th data-num>{t('tours.budgetColNet')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {budget.byCurrency.map((row) => (
                            <tr key={row.currency}>
                              <td><strong>{row.currency}</strong></td>
                              <td data-num>{row.revenue.toLocaleString()}</td>
                              <td data-num>{row.expenses.toLocaleString()}</td>
                              <td data-num style={{ color: row.net < 0 ? 'var(--danger)' : (row.net > 0 ? 'var(--success)' : undefined), fontWeight: 600 }}>
                                {row.net.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
